import { salsaConfig } from '@/utils/environment';
import { useQuery } from '@tanstack/react-query';

interface SalsaManifest {
  products: string[];
}

interface UseSalsaManifestResult {
  /** Set of product IDs that have deployed SPAs in Salsa. Empty while loading or if manifest is empty. */
  availableProducts: Set<string>;
  /** True once the manifest has been fetched (regardless of success/failure). */
  loaded: boolean;
  loading: boolean;
}

/**
 * Fetches the Salsa product manifest to determine which microproducts
 * actually have deployed SPAs. Used to filter sidebar items so users
 * don't see products that would lead to "Product not available" pages.
 *
 * Falls back gracefully: if the manifest fails to load, all products
 * are shown (no filtering applied).
 */
export interface UseSalsaManifestOptions {
  /** Set false to skip the fetch (e.g. nothing to filter). Defaults to true. */
  enabled?: boolean;
}

export const useSalsaManifest = (
  options?: UseSalsaManifestOptions,
): UseSalsaManifestResult => {
  const { data, isLoading } = useQuery({
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const response = await fetch(`${salsaConfig.webUrl}/apps/manifest.json`, {
        signal,
      });
      if (!response.ok) {
        throw new Error(`Manifest fetch failed: ${response.status}`);
      }
      return (await response.json()) as SalsaManifest;
    },
    queryKey: ['salsa-manifest', salsaConfig.webUrl],
    staleTime: 5 * 60 * 1000,
  });

  return {
    availableProducts: new Set(data?.products ?? []),
    // On fetch error, data stays undefined → loaded = false. This is an
    // intentional graceful fallback: if the manifest fails to load, consumers
    // treat it as "not loaded" and show all products unfiltered rather than
    // filtering to an empty set.
    loaded: !isLoading && data !== undefined,
    loading: isLoading,
  };
};
